import { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { AIPromptHistoryModal } from './AIPromptHistoryModal';
import {
  getAIPrompt,
  getAIPrompts,
  saveAIPrompt,
  snapshotAIPromptVersion,
  type AIPromptType,
  type AIPromptConfig,
  type AIPromptVersion,
} from '../../services/firebase';
import toast from 'react-hot-toast';
import { useAnthropicModels, type ModelOption } from '../../hooks/useAnthropicModels';

const PROMPT_DEFINITIONS: Record<
  AIPromptType,
  {
    name: string;
    description: string;
    inputFields: Array<{ name: string; description: string }>;
    outputFields: Array<{ name: string; type: string; description: string }>;
    /** Model for a slot with none saved; DEFAULT_MODEL otherwise. Mirrors the server's DEFAULT_MODEL_BY_TYPE. */
    defaultModel?: string;
  }
> = {
  enrichReference: {
    name: 'Reference Enrichment',
    description: 'Used when enriching watch reference data with AI. Suggests values for empty fields like movement type, case material, description, etc.',
    inputFields: [
      { name: 'reference', description: 'Watch reference number' },
      { name: 'model', description: 'Model name' },
      { name: 'collection', description: 'Collection name' },
      { name: 'description', description: 'Product description (may be non-English)' },
      { name: 'calibre', description: 'Calibre/movement name' },
      { name: 'movementType', description: 'Current movement type if set' },
      { name: 'caseSize', description: 'Current case size if set' },
      { name: 'waterRes', description: 'Current water resistance if set' },
      { name: 'caseMaterial', description: 'Current case material if set' },
      { name: 'dialColor', description: 'Current dial color if set' },
      { name: 'occasion', description: 'Current occasion/style categories if set' },
      { name: 'brand', description: 'Brand name' },
    ],
    outputFields: [
      // Movement
      { name: 'calibre', type: 'string', description: 'Calibre/movement name (e.g., "Sellita SW200-1", "ETA 2824-2", "Rolex 3285")' },
      { name: 'movementType', type: 'string', description: '"automatic" | "manual" | "quartz" | "solar" | "spring_drive" | "kinetic" | "connected_quartz"' },
      // Case
      { name: 'caseMaterial', type: 'string', description: 'e.g., "steel", "titanium", "gold", "ceramic"' },
      { name: 'caseShape', type: 'string', description: 'e.g., "round", "tonneau", "cushion", "rectangular", "square", "octagonal"' },
      { name: 'crystal', type: 'string[]', description: 'Material AND features together: "sapphire_crystal", "mineral_glass", "plexiglass", "cyclops_lens", "domed", "flat" (lookup_crystal_materials)' },
      { name: 'crownType', type: 'string[]', description: 'Attachment + sealing system + shape together: "screw_down", "push_pull", "twinlock", "triplock", "onion", "cabochon" (lookup_crown_types)' },
      { name: 'caseFinish', type: 'string', description: '"brushed" | "polished" | "bead_blasted" | "satin"' },
      { name: 'caseBack', type: 'string', description: '"display" | "solid" | "screw_down"' },
      { name: 'caseCoating', type: 'string', description: 'e.g., "DLC", "PVD", "none"' },
      { name: 'waterRes', type: 'string', description: 'Water resistance in meters (just number, e.g., "200")' },
      { name: 'caseSize', type: 'string', description: 'Diameter in mm (just number, e.g., "42")' },
      { name: 'caseHeight', type: 'string', description: 'Thickness in mm (just number, e.g., "13.5")' },
      { name: 'caseWeight', type: 'string', description: 'Weight in grams (just number, e.g., "175")' },
      { name: 'lugWidth', type: 'string', description: 'Lug width in mm (just number)' },
      { name: 'lugToLug', type: 'string', description: 'Lug-to-lug distance in mm (just number)' },
      { name: 'bezelType', type: 'string', description: '"unidirectional_rotating" | "bidirectional_rotating" | "fixed"' },
      { name: 'bezelMaterial', type: 'string', description: 'Bezel material e.g., "ceramic", "steel", "aluminium" — persists to the ref\'s `bezel` field' },
      { name: 'bezelColors', type: 'string[]', description: 'Bezel/insert colors, MULTI: "Pepsi" is ["red", "blue"]' },
      // Dial & hands
      { name: 'dialColor', type: 'string', description: 'e.g., "black", "blue", "silver", "green"' },
      { name: 'dialMaterial', type: 'string', description: 'e.g., "lacquer", "enamel", "mother_of_pearl", "meteorite"' },
      { name: 'dialFinish', type: 'string', description: 'Surface treatment only — e.g., "sunburst", "matte", "glossy", "guilloche", "tapisserie"' },
      { name: 'dialFeatures', type: 'string[]', description: 'Structural/decorative dial attributes (lookup_dial_features) — skeletonized, inlays, gem setting. Not the material, not the finish' },
      { name: 'dialIndexes', type: 'string[]', description: 'Marker TYPE — how the markers are made: "applied", "printed", "painted", "sandwich", "engraved" (lookup_dial_marker_type)' },
      { name: 'dialNumerals', type: 'string[]', description: 'Marker STYLE — what the markers are: "arabic", "roman", "stick", "dot", "baton" (lookup_dial_numerals)' },
      { name: 'hands', type: 'string', description: 'e.g., "dauphine", "baton", "mercedes", "cathedral", "sword", "leaf", "alpha"' },
      { name: 'handsColor', type: 'string', description: 'Hands color e.g., "silver", "blued", "gold", "white"' },
      { name: 'hasLume', type: 'boolean', description: 'Whether dial/hands have luminous material' },
      { name: 'lumePresence', type: 'string', description: 'WHERE the lume sits: "hands_only" | "markers_only" | "hands_and_markers" | "full_dial" | "none" (lookup_lume_presence)' },
      { name: 'lumeMaterial', type: 'string', description: 'WHAT the compound is: "super_luminova" | "tritium" | "radium" | "mixed" (lookup_lume_material)' },
      { name: 'lumeDayColor', type: 'string', description: 'Unlit daylight color: "white" | "cream" | "off_white" | "brown" (lookup_lume_day_color)' },
      { name: 'lumeGlowColor', type: 'string', description: 'Color it glows in the dark — NOT the day color: "green" | "blue" | "aqua" | "mixed" (lookup_lume_glow_color)' },
      { name: 'subdials', type: 'number', description: 'Number of subdials (for chronographs, e.g., 2 or 3)' },
      { name: 'subdialTypes', type: 'string[]', description: 'WHAT each subdial does — one slug per register, e.g. ["chronograph_minutes", "running_seconds"]. NOT the count (lookup_dial_subdials)' },
      { name: 'chapterRing', type: 'string', description: 'The ring between dial and crystal: "rehaut" | "internal_chapter_ring" | "flange" | "stepped" | "none" (lookup_chapter_ring_type)' },
      { name: 'minuteTrack', type: 'string', description: 'Style of the minute markings: "railroad" | "hash" | "printed" | "applied" | "mixed" | "none" (lookup_minute_track_type)' },
      { name: 'dialScales', type: 'string[]', description: 'Measurement scales, dial OR bezel: ["tachymeter", "telemeter"]. Most watches have none (lookup_dial_scales)' },
      { name: 'logoType', type: 'string', description: 'How the brand mark is applied: "applied" | "printed" | "embossed" | "relief" | "mixed" | "none" (lookup_dial_logo_type)' },
      // Strap
      { name: 'strapMaterial', type: 'string', description: '"leather" | "steel_bracelet" | "rubber" | "nato" | "canvas" | "titanium_bracelet"' },
      { name: 'strapColor', type: 'string', description: 'Strap/bracelet color' },
      { name: 'strapWidth', type: 'string', description: 'Strap width in mm (just number)' },
      { name: 'strapBuckleType', type: 'string', description: '"pin_buckle" | "deployant" | "butterfly" | "fold_over"' },
      { name: 'isMicroAdjustable', type: 'boolean', description: 'Whether bracelet has micro-adjustment' },
      { name: 'hasIntegratedBracelet', type: 'boolean', description: 'Bracelet is fused to the case (Royal Oak, Nautilus, PRX) — TOP-LEVEL, not part of the strap object' },
      // Production
      { name: 'releaseYear', type: 'number', description: 'Year the reference was released' },
      { name: 'discontinued', type: 'boolean', description: 'Whether this reference is discontinued' },
      { name: 'specialEdition', type: 'boolean', description: 'Whether this is a special/anniversary/commemorative edition' },
      { name: 'isLimited', type: 'boolean', description: 'Whether this is a limited edition' },
      { name: 'limitedEditionCount', type: 'number', description: 'Edition size (e.g., 500 for "limited to 500 pieces")' },
      // Classification
      { name: 'watchType', type: 'string', description: '"analog" | "digital" | "ana-digi" | "smartwatch"' },
      { name: 'isChronograph', type: 'boolean', description: 'Whether watch has chronograph' },
      { name: 'isGMT', type: 'boolean', description: 'Whether watch has GMT function' },
      { name: 'functions', type: 'string[]', description: 'List of complications/functions e.g., ["chronograph", "date", "moon_phase", "GMT"]' },
      { name: 'occasion', type: 'string[]', description: 'Watch occasion/style categories e.g., ["diver", "dress", "field", "pilot", "aviation", "sports", "tool", "everyday"]' },
      { name: 'collection', type: 'string', description: 'Collection name' },
      { name: 'series', type: 'string', description: 'Series/line name' },
      { name: 'nickname', type: 'string', description: 'Common watch nickname (e.g., "Pepsi", "Batman", "Hulk")' },
      { name: 'description', type: 'string', description: 'Watch description (2-4 sentences)' },
      // Electronics/Module (for digital, smartwatch, connected watches)
      { name: 'elecModule', type: 'string', description: 'Module number (e.g., "3159", "WH-1000XM5")' },
      { name: 'elecBattery', type: 'string', description: 'Battery type (e.g., "CR2025", "Li-ion rechargeable")' },
      { name: 'elecBatteryLife', type: 'string', description: 'Battery life (e.g., "10 years", "Up to 14 days", "24 hours GPS mode")' },
      { name: 'elecChargeTime', type: 'string', description: 'Charge time (e.g., "2 hours", "About 2.5 hours")' },
      { name: 'elecDisplay', type: 'string', description: 'Display type (e.g., "AMOLED", "LCD", "E-Ink")' },
      { name: 'elecLcdType', type: 'string', description: 'LCD type (e.g., "STN", "TFT", "MIP")' },
      { name: 'elecLightType', type: 'string', description: 'Backlight/light type (e.g., "LED", "Auto EL")' },
      { name: 'elecDisplayColor', type: 'string', description: 'Display background colour slug (lookup_display_colors) — segment LCD/LED only' },
      { name: 'elecTextColor', type: 'string', description: 'Digit/segment colour slug (lookup_text_colors) — segment LCD/LED only' },
      { name: 'elecBacklightColor', type: 'string', description: 'Backlight colour slug (lookup_text_colors) — the colour, not the lighting technology' },
    ],
  },
  enrichBrand: {
    name: 'Brand Enrichment',
    description: 'Used when enriching watch brand data. Researches the brand with web search (its own site first, then reputable watch press) and proposes its description, founding year, location, founder, ownership, lifecycle status and storefront platform.',
    inputFields: [
      { name: 'name', description: 'Brand name' },
      { name: 'displayName', description: 'Display name if different' },
      { name: 'country', description: 'Current country if set' },
      { name: 'founded', description: 'Current founded year if set' },
      { name: 'mainWebsite', description: 'Brand website URL' },
      { name: 'description', description: 'Current description if set' },
      { name: 'aiDescription', description: 'Current AI description if set' },
      { name: 'information', description: 'Additional brand information' },
      { name: 'shopifyEnabled', description: 'Whether the brand has a Shopify import source configured (derived from import-sources config)' },
      { name: 'glyphDescription', description: 'Current dial-glyph description if set' },
      { name: 'city / stateRegion', description: 'Current headquarters city and region if set' },
      { name: 'assemblyLocation / designLocation', description: 'Current assembly and design locations if set' },
      { name: 'instagramUrl', description: 'Current Instagram profile URL if set' },
      { name: 'ecommercePlatform', description: 'Current storefront platform if set' },
      { name: 'isMicroBrand', description: 'Sent only when the brand is already marked a microbrand' },
      { name: 'founder.name', description: 'Current founder if set' },
      { name: 'parentOrg', description: 'Current parent organization if set' },
      { name: 'status', description: 'Current lifecycle status, when it is not the default (active)' },
      { name: 'ownershipType / yearRevived / predecessorBrand', description: 'Current values if set' },
    ],
    outputFields: [
      { name: 'aiDescription', type: 'string', description: 'Brand description for mobile app (2-4 sentences)' },
      { name: 'founded', type: 'number', description: 'Year brand was founded' },
      { name: 'country', type: 'string', description: 'Country of origin' },
      { name: 'mainAddress', type: 'string', description: 'Brand headquarters address' },
      { name: 'mainWebsite', type: 'string', description: 'Brand official website URL' },
      { name: 'parentOrg', type: 'string', description: 'Parent organization/conglomerate (e.g., "Swatch Group", "LVMH")' },
      { name: 'city', type: 'string', description: 'Primary headquarters city' },
      { name: 'stateRegion', type: 'string', description: 'Sub-national region of the headquarters (US state, UK county, province…)' },
      { name: 'assemblyLocation', type: 'string', description: 'Where the watches are assembled' },
      { name: 'designLocation', type: 'string', description: 'Where the watches are designed' },
      { name: 'instagramUrl', type: 'string', description: 'Full https URL of the brand\'s Instagram profile' },
      { name: 'founder.name', type: 'string', description: 'Founder name; co-founders in one string' },
      { name: 'isMicroBrand', type: 'boolean', description: 'Small, enthusiast-run, direct-to-consumer brand' },
      { name: 'ownershipType', type: 'string', description: 'independent | family-owned | private-equity | public | subsidiary' },
      { name: 'status', type: 'string', description: 'active | dormant | defunct | revived | absorbed' },
      { name: 'yearRevived', type: 'number', description: 'Year the current revival launched — only when status is revived' },
      { name: 'predecessorBrand', type: 'string', description: 'The earlier brand this one revives — only when status is revived' },
      { name: 'ecommercePlatform', type: 'string', description: 'shopify | woocommerce | wix | squarespace | bigcommerce | magento | custom' },
      { name: 'isShopify', type: 'boolean', description: 'Kept for older callers: true when ecommercePlatform is shopify' },
      { name: 'glyphDescription', type: 'string', description: 'One-sentence description of the brand\'s distinctive dial glyph, for AI vision matching (null when the dial carries only the brand name)' },
    ],
  },
  enrichBrandHistory: {
    name: 'Brand History Timeline',
    description:
      'Used by the Timeline section on a brand. Proposes a chronological array of history events (founding, product lines, reissues, ownership changes, revivals) for the curator to review event by event. Deliberately worded for a BRAND rather than a movement maker, and warns about revived names whose modern owner is unrelated to the historical firm.',
    inputFields: [
      { name: 'name', description: 'Brand name (displayName preferred)' },
      { name: 'country', description: 'Country, when set on the document' },
      { name: 'founded', description: 'Founded year, when set' },
      { name: 'parentOrg', description: 'Parent organisation, when set' },
      { name: 'description', description: 'Current description, when set' },
      { name: 'history', description: 'Events already recorded — the model is asked to EXTEND rather than restate these' },
      { name: 'additionalContext', description: 'Curator-pasted research blocks, treated as ground truth' },
      { name: 'sourceUrlContent', description: 'Text fetched from a curator-supplied source URL' },
    ],
    outputFields: [
      { name: 'date', type: 'string', description: 'Free-form date STRING, never a calendar date' },
      { name: 'title', type: 'string', description: '10 words or fewer' },
      { name: 'description', type: 'string', description: '2-3 sentences of plain prose; the callable wraps it in <p>' },
      { name: 'category', type: 'string', description: 'founding | product | milestone | acquisition | absorption | closure | other' },
      { name: 'sourceUrl', type: 'string', description: 'Source consulted, omitted rather than invented' },
      { name: 'imageSuggestion', type: 'string', description: 'A DESCRIPTION of the photo the event needs — never a URL' },
      { name: 'confidence', type: 'string', description: 'high | medium | low, per event; stored as 0.9 / 0.6 / 0.3' },
    ],
  },
  enrichParentOrg: {
    name: 'Parent Organization Basics',
    description:
      'Used by "AI Enrich Basics" on a parent organization. Proposes corporate fields — legal name, short name, description, domicile, formation year, website, alternative names, abbreviations, status — each with a confidence, for the curator to accept field by field. Deliberately worded for a HOLDING COMPANY rather than a brand: the founding year it asks for is the organization\'s own, which is routinely confused with its oldest brand\'s.',
    inputFields: [
      { name: 'name', description: 'Current name on the document' },
      { name: 'displayName', description: 'Short name, when set' },
      { name: 'description', description: 'Current description, when set' },
      { name: 'country', description: 'Domicile, when set' },
      { name: 'founded', description: 'Formation year, when set' },
      { name: 'website', description: 'Corporate website, when set' },
      { name: 'alternativeNames', description: 'Legacy and alternative names already recorded' },
      { name: 'abbreviations', description: 'Short forms already recorded' },
      { name: 'status', description: 'active | defunct | divested, when set' },
    ],
    outputFields: [
      { name: 'name', type: 'string', description: 'Full legal name — proposed only when the legal form is known' },
      { name: 'displayName', type: 'string', description: 'Short name in common use' },
      { name: 'description', type: 'string', description: '2-4 sentences of plain prose' },
      { name: 'country', type: 'string', description: "The ORGANIZATION's domicile, not where its brands are made" },
      { name: 'founded', type: 'number', description: "Year the ORGANIZATION formed — not its oldest brand's" },
      { name: 'website', type: 'string', description: 'Corporate site, usually distinct from any brand site' },
      { name: 'alternativeNames', type: 'string[]', description: 'Historical / pre-merger names, used to match legacy records' },
      { name: 'abbreviations', type: 'string[]', description: 'Short forms such as SSIH, ASUAG' },
      { name: 'status', type: 'string', description: 'active | defunct | divested' },
      { name: 'confidence', type: 'string', description: 'high | medium | low, per field' },
    ],
  },
  enrichParentOrgHistory: {
    name: 'Parent Organization History Timeline',
    description:
      'Used by the Timeline section on a parent organization. Proposes a chronological array of events in the OWNER\'s story — formations, mergers, restructurings, changes of control, industry crises — for the curator to review event by event. Individual acquisitions belong to the Ownership Events prompt instead; this one covers a purchase only where it changed the organization.',
    inputFields: [
      { name: 'name', description: 'Organization name (displayName preferred)' },
      { name: 'legalName', description: 'Full legal name, when it differs from the display name' },
      { name: 'country', description: 'Domicile, when set' },
      { name: 'founded', description: 'Formation year, when set' },
      { name: 'status', description: 'active | defunct | divested, when set' },
      { name: 'alternativeNames', description: 'Historical names — what a historian would search under' },
      { name: 'description', description: 'Current description, when set' },
      { name: 'history', description: 'Events already recorded — the model is asked to EXTEND rather than restate these' },
      { name: 'additionalContext', description: 'Curator-pasted research blocks, treated as ground truth' },
      { name: 'sourceUrlContent', description: 'Text fetched from a curator-supplied source URL' },
    ],
    outputFields: [
      { name: 'date', type: 'string', description: 'Free-form date STRING, never a calendar date' },
      { name: 'title', type: 'string', description: '10 words or fewer' },
      { name: 'description', type: 'string', description: '2-3 sentences of plain prose; the callable wraps it in <p>' },
      { name: 'category', type: 'string', description: 'founding | product | milestone | acquisition | absorption | closure | other' },
      { name: 'sourceUrl', type: 'string', description: 'Source consulted, omitted rather than invented' },
      { name: 'imageSuggestion', type: 'string', description: 'A DESCRIPTION of the photo the event needs — never a URL' },
      { name: 'confidence', type: 'string', description: 'high | medium | low, per event; stored as 0.9 / 0.6 / 0.3' },
    ],
  },
  enrichReferenceLight: {
    name: 'Reference Metadata-Only Enrichment',
    description:
      'Narrow enrichment for release year + production dates, plus four discrete edition facts (discontinued, special edition, limited edition + count). Description is read as evidence, never written. No image analysis. Routed to Claude Haiku 5.5 for cost.',
    inputFields: [
      { name: 'reference', description: 'Watch reference number' },
      { name: 'model', description: 'Model name' },
      { name: 'collection', description: 'Collection name' },
      { name: 'series', description: 'Series, when set' },
      { name: 'productName', description: 'Product name, when set' },
      { name: 'nickname', description: 'Nickname, when set' },
      { name: 'description', description: 'Product description (may be non-English)' },
      { name: 'watchType', description: 'Watch type, when set' },
      { name: 'production', description: 'Stored production info (releaseYear, startYear, endYear, isLimited, limitedSize)' },
      { name: 'knownDiscontinuation', description: 'Whether the reference is known to be discontinued' },
      { name: 'specialEdition', description: 'Whether this is a special edition' },
      { name: 'siblingReferences', description: 'Real catalogue siblings in the same collection, dated ones first, each with its releaseYear when known' },
      { name: 'brand', description: 'Brand name' },
      { name: 'brandFounded', description: "The brand's founded year (WatchBrand.founded) — the floor for every year" },
      { name: 'productUrl', description: 'The product page URL (its text, when fetched, arrives as SOURCE URL CONTENT)' },
      { name: 'brandTimeline', description: "Up to 15 of the brand's dated product events: { year, title }" },
    ],
    outputFields: [
      { name: 'releaseYear', type: 'number', description: 'Year the reference was released; best guess with low confidence over null; never before brandFounded' },
      { name: 'startYear', type: 'number', description: 'First production year; never before brandFounded' },
      { name: 'endYear', type: 'number', description: 'Last production year, only when discontinued; never before brandFounded' },
      { name: 'discontinued', type: 'boolean', description: 'Known to be discontinued (saved as knownDiscontinuation)' },
      { name: 'specialEdition', type: 'boolean', description: 'A special / anniversary / commemorative edition' },
      { name: 'isLimited', type: 'boolean', description: "A numbered limited edition — needs a count or a 'limited edition of N' statement, never the bare word 'limited'" },
      { name: 'limitedEditionCount', type: 'number', description: 'Edition size, a whole number of at least 2 (saved as production.limitedSize)' },
    ],
    defaultModel: 'claude-haiku-5-5',
  },
  enrichReferenceVariant: {
    name: 'Reference Enrichment — Variant',
    description:
      'Used by Bulk AI Enrich for VARIANT references. Asks ONLY for the six variant-level fields (the ones the variant aggregates on the parent are built from), with the parent reference as settled context and the shop\'s option label as the strongest evidence. Any other key in the answer is discarded server-side.',
    inputFields: [
      { name: 'parentData', description: "The parent reference's stored spec (settled context)" },
      { name: 'variantLabel', description: 'The shop\'s option label, e.g. "Satin Blue Iris"' },
      { name: 'referenceData', description: "The variant's own stored fields" },
      { name: 'sourceUrlContent', description: "Text fetched from the variant's product page" },
      { name: 'taggedImages', description: "The variant's images" },
    ],
    outputFields: [
      { name: 'dialColor', type: 'string', description: 'lookup_dial_colors slug' },
      { name: 'dialFinish', type: 'string', description: 'lookup_dial_finish slug' },
      { name: 'dialStyle', type: 'string', description: 'lookup_dial_styles slug' },
      { name: 'caseMaterial', type: 'string', description: 'lookup_case_materials slug' },
      { name: 'strapMaterial', type: 'string', description: 'lookup_strap_materials slug' },
      { name: 'calibre', type: 'string', description: 'Canonical calibre name' },
    ],
  },
  enrichParentOrgOwnershipEvents: {
    name: 'Parent Organization Ownership Events',
    description:
      'Used by "AI Enrich Ownership Events" on a parent organization. Proposes STRUCTURED acquisitions, divestitures, absorptions, foundings and renamings, each naming the company involved. Targets come back as NAMES, never ids: the admin resolves each against the brand / manufacturer / organization catalogue before it can be applied, because a model cannot know a document slug. Capped at 40 events.',
    inputFields: [
      { name: 'name', description: 'Organization name (displayName preferred)' },
      { name: 'legalName', description: 'Full legal name, when it differs from the display name' },
      { name: 'country', description: 'Domicile, when set' },
      { name: 'founded', description: 'Formation year, when set' },
      { name: 'description', description: 'Current description, when set' },
      { name: 'ownershipEvents', description: 'Events already recorded — the model is asked to EXTEND rather than restate these' },
      { name: 'additionalContext', description: 'Curator-pasted research blocks, treated as ground truth' },
      { name: 'sourceUrlContent', description: 'Text fetched from a curator-supplied source URL' },
    ],
    outputFields: [
      { name: 'date', type: 'string', description: 'Free-form date STRING, never a calendar date' },
      { name: 'type', type: 'string', description: 'acquired | divested | acquired_by | divested_from | founded | absorbed | renamed | other' },
      { name: 'targetType', type: 'string', description: 'brand | manufacturer | parentOrg' },
      { name: 'targetName', type: 'string', description: 'Company NAME only — resolved to an id by the curator, never an id from the model' },
      { name: 'previousOwnerName', type: 'string', description: 'Who held it immediately before, when known' },
      { name: 'description', type: 'string', description: 'One or two sentences of plain prose' },
      { name: 'sourceUrl', type: 'string', description: 'Source consulted, omitted rather than invented' },
      { name: 'confidence', type: 'string', description: 'high | medium | low, per event; stored as 0.9 / 0.6 / 0.3' },
    ],
  },
  enrichManufacturerHistory: {
    name: 'Manufacturer History Timeline',
    description:
      'Used by the Timeline section on a manufacturer. Proposes a chronological array of history events (founding, products, acquisitions, closures) for the curator to review event by event. Confidence is returned per EVENT as high/medium/low and stored as a 0..1 number.',
    inputFields: [
      { name: 'name', description: 'Manufacturer name' },
      { name: 'country', description: 'Country, when set on the document' },
      { name: 'founded', description: 'Founded year, when set on the document' },
      { name: 'description', description: 'Current description, when set' },
      { name: 'history', description: 'Events already recorded — the model is asked to EXTEND rather than restate these' },
      { name: 'additionalContext', description: 'Curator-pasted research blocks, treated as ground truth' },
      { name: 'sourceUrlContent', description: 'Text fetched from a curator-supplied source URL' },
    ],
    outputFields: [
      { name: 'date', type: 'string', description: 'Free-form date STRING, never a calendar date — "1912", "1930s", "circa 1885"' },
      { name: 'title', type: 'string', description: '10 words or fewer' },
      { name: 'description', type: 'string', description: '2-3 sentences of plain prose; the callable wraps it in <p> for the rich-text field' },
      { name: 'category', type: 'string', description: 'founding | product | milestone | acquisition | absorption | closure | other' },
      { name: 'sourceUrl', type: 'string', description: 'Source consulted, omitted rather than invented' },
      { name: 'imageSuggestion', type: 'string', description: 'A DESCRIPTION of the photo the event needs — never a URL' },
      { name: 'confidence', type: 'string', description: 'high | medium | low, per event; stored as 0.9 / 0.6 / 0.3' },
    ],
  },
  enrichMovementManufacturer: {
    name: 'Movement Manufacturer Enrichment',
    description: 'Used when enriching movement manufacturer data (ETA, Sellita, Miyota, in-house, etc.). Generates descriptions, identifies founding year, country, parent organization, and website.',
    inputFields: [
      { name: 'name', description: 'Canonical manufacturer name (e.g., "ETA SA Manufacture Horlogère Suisse")' },
      { name: 'displayName', description: 'Short display name if different (e.g., "ETA")' },
      { name: 'description', description: 'Current factual description if set' },
      { name: 'aiDescription', description: 'Current AI description if set' },
      { name: 'country', description: 'Current country if set' },
      { name: 'founded', description: 'Current founded year if set' },
      { name: 'website', description: 'Current website URL if set' },
      { name: 'parentOrg', description: 'Current parent organization if set' },
      { name: 'city / stateRegion / assemblyLocation', description: 'Current location details if set' },
      { name: 'instagramUrl', description: 'Current Instagram profile URL if set' },
      { name: 'founder.name', description: 'Current founder if set' },
      { name: 'ownershipType / status', description: 'Current ownership type and lifecycle status if set' },
      { name: 'yearRevived / yearCeased / predecessorManufacturer', description: 'Current values if set' },
    ],
    outputFields: [
      { name: 'description', type: 'string', description: 'Factual description (2-3 sentences). Covers what they make, where, parent group relationship.' },
      { name: 'aiDescription', type: 'string', description: 'Rich mobile-app description (3-5 sentences). Covers history, notable calibres, technical reputation, brands that use them.' },
      { name: 'country', type: 'string', description: 'Country of origin (e.g., "Switzerland", "Japan", "Germany")' },
      { name: 'founded', type: 'number', description: 'Year manufacturer was founded' },
      { name: 'website', type: 'string', description: 'Official website URL' },
      { name: 'parentOrg', type: 'string', description: 'Parent organization/conglomerate (e.g., "Swatch Group" for ETA, "Citizen Holdings" for Miyota). Empty for independent makers.' },
      { name: 'city', type: 'string', description: 'Primary headquarters city' },
      { name: 'stateRegion', type: 'string', description: 'Sub-national region of the headquarters (Swiss canton, Japanese prefecture…)' },
      { name: 'assemblyLocation', type: 'string', description: 'Where the movements are actually assembled' },
      { name: 'instagramUrl', type: 'string', description: 'Full https URL of the Instagram profile, when there is one' },
      { name: 'founder.name', type: 'string', description: 'Founder name, only when the sources clearly name one' },
      { name: 'ownershipType', type: 'string', description: 'independent | family-owned | private-equity | public | subsidiary' },
      { name: 'status', type: 'string', description: 'active | dormant | defunct | revived | absorbed' },
      { name: 'yearRevived', type: 'number', description: 'Year the current revival launched — only when status is revived' },
      { name: 'yearCeased', type: 'number', description: 'Year production ended — only when status is defunct or absorbed' },
      { name: 'predecessorManufacturer', type: 'string', description: 'The earlier manufacturer this one revives — only when status is revived' },
    ],
  },
  enrichCalibre: {
    name: 'Calibre Identification',
    description: 'Used to identify watch calibres/movements from potentially mangled or partial names. Returns canonical name and specifications.',
    inputFields: [
      { name: 'calibreName', description: 'Calibre name (possibly mangled/partial/foreign)' },
      { name: 'brandName', description: 'Watch brand name for context' },
      { name: 'referenceInfo', description: 'User-supplied context pasted upfront — treated as ground truth' },
      { name: 'existingData', description: 'Currently set / user-corrected fields (movementType, jewels, etc.) — AI must not contradict these' },
    ],
    outputFields: [
      { name: 'canonicalName', type: 'string', description: 'Correct calibre name' },
      { name: 'movementType', type: 'string', description: '"automatic" | "manual" | "quartz" | "kinetic" | "solar"' },
      { name: 'jewels', type: 'number', description: 'Number of jewels' },
      { name: 'powerReserveHours', type: 'number', description: 'Power reserve in hours' },
      { name: 'frequencyVph', type: 'number', description: 'Vibrations per hour' },
      { name: 'diameter', type: 'number', description: 'Diameter in mm' },
      { name: 'height', type: 'number', description: 'Height in mm' },
      { name: 'hacking', type: 'boolean', description: 'Has hacking seconds' },
      { name: 'manualWinding', type: 'boolean', description: 'Supports manual winding' },
      { name: 'quickSetDate', type: 'boolean', description: 'Has quick-set date' },
      { name: 'isChronograph', type: 'boolean', description: 'Has chronograph' },
      { name: 'isGMT', type: 'boolean', description: 'Has GMT function' },
      { name: 'isChronometer', type: 'boolean', description: 'Chronometer-certified' },
      { name: 'isCosc', type: 'boolean', description: 'COSC-certified' },
      { name: 'isGlashuetteRegulation', type: 'boolean', description: 'Glashütte Quality Seal (only Glashütte brands)' },
      { name: 'isGenevaSeal', type: 'boolean', description: 'Geneva Seal / Poinçon de Genève (only Geneva brands)' },
      { name: 'isGrandSeikoStandard', type: 'boolean', description: 'Grand Seiko Standard (only Grand Seiko)' },
      { name: 'isMETAS', type: 'boolean', description: 'METAS Master Chronometer (only Omega)' },
      { name: 'isInHouse', type: 'boolean', description: 'In-house movement' },
      { name: 'columnWheel', type: 'boolean', description: 'Has column wheel' },
      { name: 'doubleBarrel', type: 'boolean', description: 'Has double barrel' },
      { name: 'manufacturer', type: 'string', description: 'Movement manufacturer (e.g., "ETA", "Sellita")' },
      { name: 'brandId', type: 'string', description: 'watchBrand doc id slug (lowercase, dash-separated). Set ONLY when single-brand; null for generic movements (ETA / Sellita / Miyota / …).' },
      { name: 'baseMovement', type: 'string', description: 'Base movement if modified' },
      { name: 'windingDirection', type: 'string', description: 'e.g., "Bidirectional", "Unidirectional"' },
      { name: 'antiShockSystem', type: 'string', description: 'e.g., "Incabloc", "KIF", "Parachrom"' },
      { name: 'countryOfManufacture', type: 'string', description: 'e.g., "Switzerland", "Japan", "Germany"' },
      { name: 'escapement', type: 'string', description: 'e.g., "Swiss lever", "Co-Axial"' },
      { name: 'regulatorSystem', type: 'string', description: 'e.g., "Etachron", "Free-sprung"' },
      { name: 'hairspring', type: 'string', description: 'e.g., "Nivaflex", "Nivarox", "Silicon"' },
      { name: 'rotorType', type: 'string', description: 'Rotor bearing/suspension or material only, e.g. "Ball-bearing", "Jewelled bearing", "Tungsten rotor". NOT the winding layout.' },
      { name: 'windingArchitecture', type: 'string', description: 'Self-winding system topology of an automatic movement (full rotor, micro-rotor, peripheral, bumper/hammer, dual rotor). Slug from lookup_winding_architecture; null for manual/quartz.' },
      { name: 'rotorMaterial', type: 'string', description: 'What the rotor is MADE OF. Slug from lookup_rotor_material; null for manual/quartz. Distinct from rotorType (its bearing) and windingArchitecture (where the mass sits).' },
      { name: 'bridgeMaterial', type: 'string', description: 'What the bridges — and the mainplate where they share a material — are made of. Slug from lookup_bridge_material; null when the two genuinely differ.' },
      { name: 'plateFinishing', type: 'string', description: 'The mainplate\'s PRIMARY surface treatment. Slug from lookup_plate_finishing. Narrower than movementFinishing (overall quality grade); other techniques go in decorations.' },
      { name: 'caseMaterialsAvailable', type: 'string[]', description: 'Case materials this calibre is OFFERED in, as lookup_case_materials slugs. Availability, not composition — empty for the unrestricted majority.' },
      { name: 'batteryType', type: 'string', description: 'For quartz/solar/kinetic, e.g., "SR920SW"' },
      { name: 'batteryLifeYears', type: 'number', description: 'Typical battery life in years' },
      { name: 'shockResistanceG', type: 'number', description: 'Shock resistance in G-force (e.g., 5000)' },
      { name: 'antiMagneticAm', type: 'number', description: 'Anti-magnetic rating in A/m (e.g., 4800)' },
      { name: 'antiMagneticGauss', type: 'number', description: 'Anti-magnetic rating in Gauss (e.g., 60)' },
      { name: 'date', type: 'string[]', description: 'Date functions (e.g., "date window", "big date", "perpetual calendar")' },
      { name: 'hands', type: 'string[]', description: 'Hand types (e.g., "central seconds", "small seconds", "24-hour hand")' },
      { name: 'chronograph', type: 'string[]', description: 'Chronograph functions (e.g., "flyback", "split-seconds", "30-minute counter")' },
      { name: 'astronomical', type: 'string[]', description: 'Astronomical complications (e.g., "moon phase", "equation of time")' },
      { name: 'acoustic', type: 'string[]', description: 'Acoustic complications (e.g., "minute repeater", "alarm", "sonnerie")' },
      { name: 'additionals', type: 'string[]', description: 'Additional functions (e.g., "power reserve indicator", "tourbillon", "world time")' },
      { name: 'description', type: 'string', description: 'Rich 3-5 sentence calibre description' },
      { name: 'sourceUrls', type: 'string[]', description: 'URLs (1-3) where specs can be verified' },
      { name: 'notes', type: 'string', description: 'Context about identification' },
    ],
  },
  moduleEnrichment: {
    name: 'Electronic Module Enrichment',
    description:
      'Used by the enrichElectronicModule callable to identify an electronic module (e.g. Casio Module 3159) and fill its spec fields. Returns a per-field map ({ value | values, confidence, reasoning }). {{LOOKUP_*}} tokens are substituted with the live lookup value lists at call time.',
    inputFields: [
      { name: 'moduleId', description: 'The saved electronicModules doc id (the callable loads the doc server-side)' },
      { name: 'name', description: 'Module name / number (e.g. "3159", "UTD-8000")' },
      { name: 'manufacturer', description: 'Manufacturer name resolved from manufacturerId, if set' },
      { name: 'generation', description: 'Current generation if set' },
      { name: 'alsoKnownAs', description: 'Existing alias list for the module' },
      { name: 'context', description: 'Optional free-text hints supplied by the operator (e.g. a linked reference blurb)' },
    ],
    outputFields: [
      { name: 'name', type: 'string', description: 'Canonical module name/number' },
      { name: 'aiDescription', type: 'string', description: 'Rich 2-4 sentence module description' },
      { name: 'displayType', type: 'string', description: 'Display type slug (from lookup_display_type)' },
      { name: 'lcdType', type: 'string', description: 'LCD type slug (from lookup_lcd_type)' },
      { name: 'lightType', type: 'string', description: 'Backlight/light type slug (from lookup_light_type)' },
      { name: 'displayColor', type: 'string', description: 'Default display background colour slug (from lookup_display_colors)' },
      { name: 'textColor', type: 'string', description: 'Default segment/text colour slug (from lookup_text_colors)' },
      { name: 'battery', type: 'string', description: 'Battery type slug (from lookup_battery_type)' },
      { name: 'batteryLifeMonths', type: 'number', description: 'Battery life in months' },
      { name: 'chargeTimeHours', type: 'number', description: 'Charge time in hours (rechargeable modules)' },
      { name: 'generation', type: 'string', description: 'Generation slug (from lookup_module_generation)' },
      { name: 'displayLayout', type: 'string', description: 'Display layout slug (from lookup_display_layout)' },
      { name: 'viewingAngle', type: 'string', description: 'Viewing angle slug (from lookup_viewing_angle)' },
      { name: 'dstSupport', type: 'string', description: 'DST support slug (from lookup_dst_support)' },
      { name: 'calendarType', type: 'string', description: 'Calendar type slug (from lookup_calendar_type)' },
      { name: 'stopwatchPrecision', type: 'string', description: 'Stopwatch precision slug (from lookup_stopwatch_precision)' },
      { name: 'powerSource', type: 'string', description: 'Power source slug (from lookup_power_source)' },
      { name: 'syncSource', type: 'string', description: 'Sync source slug (from lookup_sync_source)' },
      { name: 'sensors', type: 'string[]', description: 'Sensor slugs (from lookup_sensors) — returned under "values"' },
      { name: 'timeSystems', type: 'string[]', description: 'Time system slugs (from lookup_time_systems) — returned under "values"' },
      { name: 'alarmTypes', type: 'string[]', description: 'Alarm type slugs (from lookup_alarm_types) — returned under "values"' },
      { name: 'source', type: 'string', description: 'Source label for the data' },
      { name: 'sourceUrl', type: 'string', description: 'URL where the spec can be verified' },
    ],
  },
  enrichWinder: {
    name: 'Winder Enrichment',
    description:
      'Used by the enrichWinder callable to identify a watch winder from its make + model and fill its spec fields. Returns a per-field map ({ value | values, confidence, reasoning }). {{LOOKUP_*}} tokens are substituted with the live lookup value lists at call time — cardinality (one slug vs an array) comes from each collection\'s isMultiSelect flag, so the prompt text never restates it.',
    inputFields: [
      { name: 'make', description: 'lookup_winder_manufacturers slug, resolved to its display name before the call. GROUND TRUTH — never renamed' },
      { name: 'model', description: 'Model name as the manufacturer writes it (e.g. "Cub Single Winder")' },
      { name: 'winderId', description: 'Optional saved winders/{id}; when present its stored non-null values are sent as ground truth' },
      { name: 'context', description: 'Optional free-text hints supplied by the operator' },
    ],
    outputFields: [
      { name: 'applianceKind', type: 'string', description: '"winder", "safe" or "display_case" — what it is PRIMARILY. Ladder: winds → winder (even if it also protects and displays); else hardened body → safe (even with a viewing window); else glazed → display_case' },
      { name: 'displayName', type: 'string', description: 'Full presentation name (e.g. "Wolf Cub Single Winder")' },
      { name: 'countryOfManufacture', type: 'string', description: 'Country of manufacture — not of headquarters' },
      { name: 'slotCount', type: 'number', description: 'Number of watch slots (1, 2, 4, 6, 12, …)' },
      { name: 'turnsPerDay', type: 'number[]', description: 'Every supported TPD setting — returned under "values"' },
      { name: 'turnsPerDayRange', type: '{min,max}', description: 'TPD as a continuous range for a programmable winder. Mutually exclusive with turnsPerDay' },
      { name: 'startDelayMaxHours', type: 'number', description: 'Max programmable delay before the first winding cycle, in hours (e.g. Wolf Atlas = 255)' },
      { name: 'isSafe', type: 'boolean', description: 'Has the security properties of a safe — a hardened body, not merely a locking case. Independent of applianceKind: true for every "safe", and for a winder built into one' },
      { name: 'safeSpecs', type: 'object', description: 'Security specs (certification, fireRatingMinutes, fireMaxInternalTempC, weightKg, lockingMechanism, lockingBoltCount, wallThicknessMm, hasAnchorPoints, insuranceCashRating). Only when isSafe is true' },
      { name: 'rotationDirection', type: 'string', description: 'Rotation direction slug (from lookup_winder_rotation_directions)' },
      { name: 'rotationProgramSlugs', type: 'string[]', description: 'Program slugs (from lookup_winder_programs) — returned under "values"' },
      { name: 'powerSourceSlugs', type: 'string[]', description: 'Power source slugs (from lookup_winder_power_sources) — returned under "values"' },
      { name: 'materialSlugs', type: 'string[]', description: 'Material slugs (from lookup_winder_materials) — returned under "values"' },
      { name: 'isQuiet', type: 'boolean', description: 'Manufacturer-claimed quiet operation' },
      { name: 'hasLock', type: 'boolean', description: 'Has a lockable case' },
      { name: 'hasDisplayCase', type: 'boolean', description: 'IS a display case — a glazed surface the watches are meant to be looked at through. NOT a dust cover. Independent of applianceKind, exactly as isSafe is' },
      { name: 'displayCaseSpecs', type: 'object', description: 'Display specs (glazing, hasUvFiltering, hasIllumination, hasHygrometer, hasHumidityControl, mountingStyle, hasDrawer). Only when hasDisplayCase is true' },
      { name: 'priceTierSlug', type: 'string', description: 'Price tier slug (from lookup_winder_price_tiers)' },
      { name: 'aliases', type: 'string[]', description: 'AKA model names for the SAME product — returned under "values"' },
      { name: 'description', type: 'string', description: '2-4 factual sentences about the winder' },
      { name: 'sourceUrls', type: 'string[]', description: 'URLs (1-3) where specs can be verified — returned under "values"' },
    ],
  },
  suggestLookupDescriptions: {
    name: 'Lookup Descriptions',
    description:
      'Used by the Lookup Manager "Suggest descriptions with AI" button. Drafts the description field for each value in a lookup collection (these feed {{LOOKUP_*}} prompt-token substitution). Tokens {{COLLECTION_DISPLAY_NAME}} and {{VALUES_LIST}} are substituted at call time.',
    inputFields: [
      { name: 'COLLECTION_DISPLAY_NAME', description: 'Human-friendly collection name, e.g. "Dial Styles"' },
      { name: 'VALUES_LIST', description: 'Rendered list of values (slug, display name, synonyms)' },
    ],
    outputFields: [
      { name: 'descriptions', type: 'object', description: 'Map of value slug to a 1-2 sentence description' },
    ],
  },
  suggestCalibreFamily: {
    name: 'Calibre Family Suggestion',
    description:
      "Used by the suggestCalibreFamily callable on the Calibre Families editor (Movement Manufacturers tab). Suggests a family's description, its structural base calibre, any cross-manufacturer lineage, and market rivals. Every field is nullable by design: the model is instructed to return nothing rather than a plausible guess, because a wrong-but-confident answer costs a curator more to undo than an empty one. Values the curator has already set are sent as ground truth and must not be overwritten or rephrased.",
    inputFields: [
      { name: 'manufacturerId', description: 'movement_manufacturers doc id — resolved to display name + country before the call' },
      { name: 'familyName', description: 'The family name being curated. GROUND TRUTH — never renamed' },
      { name: 'memberCalibreIds', description: 'Up to 40 member calibres, sent as id/name/specs lines. The ONLY ids the model may return for baseCalibre' },
      { name: 'existingDescription', description: 'Curator\'s current description, sent in the CURATOR VALUES block as ground truth' },
      { name: 'existingBaseCalibre', description: 'Resolved display name of the chosen base calibre (not its id)' },
      { name: 'existingInspiredBy', description: 'Resolved display label of the chosen inspired-by reference (not its id)' },
      { name: 'existingCompetitors', description: 'Display labels of competitors already on the family — the model returns only NEW ones' },
    ],
    outputFields: [
      { name: 'description', type: 'object | null', description: '2-4 sentences specific to this family, with confidence + reasoning. Null when everything sayable would be generic' },
      { name: 'baseCalibre', type: 'object | null', description: 'Structural root within the family. calibreId is kept only when it matches a supplied member; otherwise the free-text name seeds the typeahead' },
      { name: 'inspiredBy', type: 'object | null', description: 'Cross-manufacturer genealogy (cloned/derived from). Ids are never resolved server-side — hint text seeds the picker' },
      { name: 'functionalCompetitor', type: 'array', description: '0-5 market rivals as free-text name + manufacturer. Never null; empty when none are well known' },
    ],
  },
  suggestManufacturerTierVocabulary: {
    name: 'Manufacturer Tier Names Suggestion',
    description:
      "Used by the Suggest tier names button on a movement manufacturer's Calibre Tier Names section. Proposes the manufacturer's official GENERIC grade names and their other spellings, grounded in the curator's list and the catalogue's own tier spellings. Add-only: never renames or removes curator entries. Brand tiers are never sent or returned. The model answers through the submit_manufacturer_tier_vocabulary tool.",
    inputFields: [
      { name: 'manufacturerId', description: 'movement_manufacturers doc id — resolved to display name + country before the call' },
      { name: 'CURATOR_VALUES', description: 'The manufacturer\'s existing GENERIC tierVocabulary entries (id, canonical, spellings, rank). Ground truth — never renamed, removed or re-ranked' },
      { name: 'CATALOGUE_EVIDENCE', description: 'From custom_calibres for this manufacturer: generic tier spellings with counts, family tier names, grade tokens in aliases. Brand-specific tiers and deprecated stubs are excluded' },
      { name: 'additionalContext', description: 'Optional curator hint typed into the review dialog' },
    ],
    outputFields: [
      { name: 'publishesGradeLadder', type: 'boolean', description: 'false (with no tiers) is a valid answer — many in-house manufacturers publish no ladder' },
      { name: 'tiers', type: 'array', description: 'canonicalName, akas, rank, description, matchesExistingEntryId, evidence, confidence — checked by cleanTierVocabOutput (suffix-like names, clashing synonyms and evidence-free rows are dropped)' },
      { name: 'notes', type: 'string', description: 'Doubts about curator names and family-specific spellings, shown to the curator; never written' },
    ],
  },
  suggestCalibreLineage: {
    name: 'Calibre Revision Line Suggestion',
    description:
      "Used by Ask AI on a manufacturer's Revision lines section and by Find revision lines (lineage Phase 5). Decides whether a set of one manufacturer's calibres are successive revisions (a revision line), concurrent grades (tiers), peers, or a mix, and proposes labels, production order, years and the current revision. Curator-set labels, years, current and placeholder flags are ground truth: the user message repeats the rule in code and the callable withholds and logs any proposal that would change one. Only for manufacturers with Supports lineage on. The model answers through the submit_calibre_lineage tool.",
    inputFields: [
      { name: 'manufacturerId', description: 'movement_manufacturers doc id; the callable rejects it unless supportsLineage is true' },
      { name: 'lineageId', description: 'An existing line: its members are sent with every recorded fact (structure locked). Absent for a Find revision lines cluster' },
      { name: 'CANDIDATE_CALIBRES', description: 'Up to 25 calibres: id, name, AKAs, key specs, family, ref count and the min/max release year of refs linked by calibreId' },
      { name: 'CURATOR_VALUES', description: 'Line label, revision labels, years (marked "(ai)" when AI-applied and revisable), CURRENT, PLACEHOLDER, membership of another line' },
      { name: 'additionalContext', description: 'Optional curator hint typed into the dialog' },
    ],
    outputFields: [
      { name: 'verdict', type: "'revision-line' | 'tiers' | 'peers' | 'mixed'", description: 'With verdictReason' },
      { name: 'baseLabel', type: 'string | null', description: 'Label for a new line (cluster runs only)' },
      { name: 'members', type: 'array', description: 'calibreId, revisionLabel, order, activeFromYear, activeUntilYear, confidence, evidence — ids outside the input are dropped, years must be 4-digit' },
      { name: 'currentCalibreId', type: 'string | null', description: 'With currentConfidence and currentReasoning' },
      { name: 'additionalLines', type: 'array', description: 'Further SEPARATE lines among the candidates (parallel variants, e.g. ETA 6498-2 beside 6498 / 6498-1): baseLabel, lineReason, successorNote, members, currentCalibreId. Each is guarded as a NEW line; a calibre is in at most one line' },
      { name: 'knownMissingRevisions', type: 'array', description: 'Revisions not in the catalogue — information only in Phase 5' },
      { name: 'stemAliases', type: 'string[]', description: 'Names the bare line is known by — information only' },
    ],
  },  lookupRefReleaseYear: {
    name: 'Reference Release Year Lookup',
    description:
      "Used by the ✨ button on a bulk-import review row whose calibre matched a revision line. One web search for the reference's first release year; the admin's revision resolver then re-runs with it. Never writes. The callable checks the year (4 digits, not before the brand's founding, not in the future) and answers through the submit_release_year tool.",
    inputFields: [
      { name: 'brandId', description: 'watchBrands doc id — resolved to display name + founded year before the call' },
      { name: 'refName', description: 'Model / reference name from the import row' },
      { name: 'refNumber', description: 'Reference number from the import row' },
    ],
    outputFields: [
      { name: 'year', type: 'integer | null', description: 'First release year, or null when no source gives one' },
      { name: 'confidence', type: "'high' | 'medium' | 'low'", description: 'Caps the confidence of the revision pick made from it' },
      { name: 'evidence', type: 'string', description: 'One sentence: what was found and where' },
    ],
    defaultModel: 'claude-haiku-5-5',
  },
};

// The model list and its session cache now live in hooks/useAnthropicModels,
// shared with the single-ref enrichment dialog's per-run picker. This editor
// takes the UNCURATED list: it sets the standing model for a prompt type and
// occasionally needs an older id pinned.
const DEFAULT_MODEL = 'claude-sonnet-4-6';
const defaultModelFor = (promptType: AIPromptType) => PROMPT_DEFINITIONS[promptType].defaultModel ?? DEFAULT_MODEL;

/**
 * Options for a model `<select>`.
 *
 * Two cases matter beyond the happy path. While loading, the only option carries
 * the currently-saved value, so the select's value still matches what's stored
 * and a save mid-load can't silently rewrite the model. Once loaded, a saved
 * value Anthropic no longer lists is prepended — otherwise the select would
 * render blank and the first interaction would change the stored model.
 */
function ModelOptions({
  options,
  loading,
  current,
}: {
  options: ModelOption[];
  loading: boolean;
  current: string;
}) {
  if (loading) {
    return <option value={current}>Loading models… ({current})</option>;
  }
  const currentIsListed = options.some((o) => o.id === current);
  return (
    <>
      {!currentIsListed && current && (
        <option value={current}>{current} (not currently listed)</option>
      )}
      {options.map((o) => (
        <option key={o.id} value={o.id} title={o.title}>
          {o.id}
        </option>
      ))}
    </>
  );
}

// No prompt texts live here. Every prompt is in Firestore
// (admin_config/ai_prompts → prompts.<type>.systemPrompt), edited on this
// screen, and the functions read ONLY that — a missing prompt makes its
// enrichment fail loudly rather than run on a stale copy. The client-side
// copies this file used to carry are gone for the same reason.

export function AIPromptsEditor() {
  const [prompts, setPrompts] = useState<Record<AIPromptType, AIPromptConfig>>({} as Record<AIPromptType, AIPromptConfig>);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState<AIPromptType | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editModel, setEditModel] = useState<string>(DEFAULT_MODEL);
  const [changeNote, setChangeNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingModelFor, setSavingModelFor] = useState<AIPromptType | null>(null);
  const [historyFor, setHistoryFor] = useState<AIPromptType | null>(null);
  const {
    options: modelOptions,
    loading: modelsLoading,
    failed: modelsFailed,
  } = useAnthropicModels();

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      const data = await getAIPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('Failed to load prompts:', error);
      toast.error('Failed to load AI prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promptType: AIPromptType) => {
    // Firestore is the only source: a missing prompt opens empty, to be written here.
    const current = prompts[promptType]?.systemPrompt || '';
    setEditValue(current);
    setEditModel(prompts[promptType]?.model || defaultModelFor(promptType));
    setChangeNote('');
    setEditingPrompt(promptType);
  };

  const handleSave = async () => {
    if (!editingPrompt) return;
    setSaving(true);
    try {
      // Snapshot the current live text BEFORE overwriting it. Read the stored
      // value fresh so we capture what's actually live, not stale local state.
      const live = await getAIPrompt(editingPrompt);
      const liveText = live?.systemPrompt ?? '';
      if (liveText.trim() && liveText !== editValue) {
        await snapshotAIPromptVersion(editingPrompt, liveText, changeNote);
      }

      await saveAIPrompt(editingPrompt, {
        name: PROMPT_DEFINITIONS[editingPrompt].name,
        description: PROMPT_DEFINITIONS[editingPrompt].description,
        systemPrompt: editValue,
        model: editModel,
        inputFields: PROMPT_DEFINITIONS[editingPrompt].inputFields,
        outputFields: PROMPT_DEFINITIONS[editingPrompt].outputFields,
      });
      setPrompts((prev) => ({
        ...prev,
        [editingPrompt]: {
          ...prev[editingPrompt],
          id: editingPrompt,
          name: PROMPT_DEFINITIONS[editingPrompt].name,
          description: PROMPT_DEFINITIONS[editingPrompt].description,
          systemPrompt: editValue,
          model: editModel,
          inputFields: PROMPT_DEFINITIONS[editingPrompt].inputFields,
          outputFields: PROMPT_DEFINITIONS[editingPrompt].outputFields,
        },
      }));
      toast.success('Prompt saved successfully');
      setChangeNote('');
      setEditingPrompt(null);
    } catch (error) {
      console.error('Failed to save prompt:', error);
      toast.error('Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  // Restore a historical version. Snapshots the current live value first (so the
  // restore itself is undoable), then writes the historical text back as live.
  const handleRestoreVersion = async (version: AIPromptVersion) => {
    const promptType = version.promptKey;
    const live = await getAIPrompt(promptType);
    const liveText = live?.systemPrompt ?? '';
    if (liveText.trim() && liveText !== version.promptText) {
      await snapshotAIPromptVersion(promptType, liveText, 'Auto-snapshot before restore');
    }

    const model = live?.model || prompts[promptType]?.model || defaultModelFor(promptType);
    await saveAIPrompt(promptType, {
      name: PROMPT_DEFINITIONS[promptType].name,
      description: PROMPT_DEFINITIONS[promptType].description,
      systemPrompt: version.promptText,
      model,
      inputFields: PROMPT_DEFINITIONS[promptType].inputFields,
      outputFields: PROMPT_DEFINITIONS[promptType].outputFields,
    });
    setPrompts((prev) => ({
      ...prev,
      [promptType]: {
        ...prev[promptType],
        id: promptType,
        name: PROMPT_DEFINITIONS[promptType].name,
        description: PROMPT_DEFINITIONS[promptType].description,
        systemPrompt: version.promptText,
        model,
        inputFields: PROMPT_DEFINITIONS[promptType].inputFields,
        outputFields: PROMPT_DEFINITIONS[promptType].outputFields,
      },
    }));
    // If the editor is open on this prompt, reflect the restored text live.
    if (editingPrompt === promptType) {
      setEditValue(version.promptText);
    }
  };

  const handleModelChange = async (promptType: AIPromptType, model: string) => {
    const existing = prompts[promptType];
    setSavingModelFor(promptType);
    try {
      // `systemPrompt` is sent ONLY when an override already exists. Falling back
      // to the client default here was the other way its stale copy reached
      // Firestore: switching the model on a prompt that had no override wrote
      // that copy in as the prompt. `saveAIPrompt` merges, so omitting the key
      // leaves the stored text — or the absence of any — exactly as it was.
      await saveAIPrompt(promptType, {
        name: PROMPT_DEFINITIONS[promptType].name,
        description: PROMPT_DEFINITIONS[promptType].description,
        ...(existing?.systemPrompt ? { systemPrompt: existing.systemPrompt } : {}),
        model,
        inputFields: PROMPT_DEFINITIONS[promptType].inputFields,
        outputFields: PROMPT_DEFINITIONS[promptType].outputFields,
      });
      setPrompts((prev) => ({
        ...prev,
        [promptType]: {
          ...prev[promptType],
          id: promptType,
          name: PROMPT_DEFINITIONS[promptType].name,
          description: PROMPT_DEFINITIONS[promptType].description,
          // Mirrors the write: the stored prompt is untouched by a model change,
          // so an override-less prompt stays override-less here too (otherwise
          // the row would claim "Customized" for a prompt nobody has edited).
          systemPrompt: existing?.systemPrompt,
          model,
          inputFields: PROMPT_DEFINITIONS[promptType].inputFields,
          outputFields: PROMPT_DEFINITIONS[promptType].outputFields,
        },
      }));
      toast.success('Model updated');
    } catch (error) {
      console.error('Failed to update model:', error);
      toast.error('Failed to update model');
    } finally {
      setSavingModelFor(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  // Derived, never listed. A hand-written array is a second registry that
  // nothing keeps in step with the first: that is how the three parent-org
  // prompts shipped in #584 defined, defaulted, wired to working buttons, and
  // absent from this screen. PROMPT_DEFINITIONS is `Record<AIPromptType, …>`,
  // so the compiler already forces it to hold every prompt type — deriving from
  // it means a new key registers itself here the moment it is defined.
  //
  // Card order is therefore PROMPT_DEFINITIONS' insertion order. To move a card,
  // move its definition.
  const promptTypes: AIPromptType[] = Object.keys(PROMPT_DEFINITIONS) as AIPromptType[];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">AI Prompts</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure the system prompts used by AI enrichment functions. Changes take effect within 5 minutes.
        </p>
      </div>

      <div className="grid gap-4">
        {promptTypes.map((promptType) => {
          const def = PROMPT_DEFINITIONS[promptType];
          const saved = prompts[promptType];
          // Firestore is the only source, so a prompt with no saved text has NO
          // prompt at all — its enrichment fails until one is written here.
          const isMissing = !saved?.systemPrompt?.trim();

          const currentModel = saved?.model || defaultModelFor(promptType);
          return (
            <div key={promptType} className="bg-white rounded-lg border p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{def.name}</h3>
                    {isMissing && (
                      <span
                        className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full"
                        title="No prompt saved in Firestore — this enrichment fails until one is written here."
                      >
                        Missing — enrichment will fail
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{def.description}</p>
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Input Fields</p>
                    <div className="flex flex-wrap gap-1">
                      {def.inputFields.map((field) => (
                        <span
                          key={field.name}
                          className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded"
                          title={field.description}
                        >
                          {field.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Output Fields</p>
                    <div className="flex flex-wrap gap-1">
                      {def.outputFields.slice(0, 8).map((field) => (
                        <span
                          key={field.name}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                          title={`${field.type}: ${field.description}`}
                        >
                          {field.name}
                        </span>
                      ))}
                      {def.outputFields.length > 8 && (
                        <span className="text-xs text-gray-400">
                          +{def.outputFields.length - 8} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Model</label>
                  <select
                    value={currentModel}
                    onChange={(e) => handleModelChange(promptType, e.target.value)}
                    disabled={savingModelFor === promptType || modelsLoading}
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
                  >
                    <ModelOptions
                      options={modelOptions}
                      loading={modelsLoading}
                      current={currentModel}
                    />
                  </select>
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(promptType)}>
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingPrompt}
        onClose={() => setEditingPrompt(null)}
        title={editingPrompt ? `Edit: ${PROMPT_DEFINITIONS[editingPrompt].name}` : 'Edit Prompt'}
        size="full"
      >
        {editingPrompt && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> The prompt tells the AI how to behave. The input and output field names (listed below) are fixed and defined in the function code.
                Only modify the instructions, not the expected field names.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model
              </label>
              <select
                value={editModel}
                onChange={(e) => setEditModel(e.target.value)}
                disabled={modelsLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
              >
                <ModelOptions
                  options={modelOptions}
                  loading={modelsLoading}
                  current={editModel}
                />
              </select>
              {modelsFailed && (
                <p className="mt-1 text-xs text-amber-700">
                  Couldn't reach Anthropic's model list — showing a local fallback,
                  which may be missing newer models.
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  System Prompt
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editingPrompt && setHistoryFor(editingPrompt)}
                >
                  History
                </Button>
              </div>
              <textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                rows={20}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Input Fields (read-only)
              </p>
              <div className="bg-teal-50/50 border border-teal-100 rounded-lg p-3">
                <div className="flex flex-wrap gap-1.5">
                  {PROMPT_DEFINITIONS[editingPrompt].inputFields.map((field) => (
                    <span
                      key={field.name}
                      className="text-xs bg-teal-100 text-teal-800 border border-teal-200 px-2 py-0.5 rounded"
                      title={field.description}
                    >
                      {field.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Output Fields (read-only)
              </p>
              <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="flex flex-wrap gap-1.5">
                  {PROMPT_DEFINITIONS[editingPrompt].outputFields.map((field) => (
                    <span
                      key={field.name}
                      className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded"
                      title={`${field.type}: ${field.description}`}
                    >
                      {field.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What changed? <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="Short note saved with the previous version's snapshot"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              {/* No "Reset to Default". It filled this box with the CLIENT's
                  copy of the prompt, which drifts from the deployed default and
                  from whatever an admin has tuned — pressing it and saving wiped
                  a curated enrichReference prompt with a 33-line stale draft.
                  Prompts are admin-authored: edit the text here, or restore an
                  earlier wording from History, above. */}
              <p className="text-xs text-gray-500">
                These prompts are admin-authored. Edit the text above and Save; to
                go back to an earlier wording use <strong>History</strong>, beside
                the System Prompt label.
              </p>
              <div className="flex justify-end items-center">
                <div className="flex gap-3">
                  <Button variant="secondary" onClick={() => setEditingPrompt(null)} disabled={saving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} loading={saving}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Version History Modal */}
      <AIPromptHistoryModal
        isOpen={!!historyFor}
        onClose={() => setHistoryFor(null)}
        promptKey={historyFor}
        promptName={historyFor ? PROMPT_DEFINITIONS[historyFor].name : ''}
        onRestore={handleRestoreVersion}
      />
    </div>
  );
}
